import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideGift } from '@ng-icons/lucide';
import { currencySymbol, formatWholeAmount } from '../../utils/currency.utils';
import { EmbedVariant } from '../settings/integration-settings/embed-snippet';

/**
 * A sketch of the customer's website showing where the shop appears: the
 * floating button in the corner, or the shop inside the page content. Drawn in
 * the company colours; it explains placement, it is not the live plugin.
 */
@Component({
  selector: 'app-embed-preview',
  imports: [NgIcon],
  providers: [provideIcons({ lucideGift })],
  templateUrl: './embed-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbedPreviewComponent {
  readonly variant = input.required<EmbedVariant>();
  readonly primaryColor = input<string | undefined>();
  readonly textColor = input<string | undefined>();
  readonly title = input<string | undefined>();
  /** Shown above the sketch. */
  readonly caption = input('Vorschau');
  /** About half the height and fewer filler lines, for a small side-by-side pair. */
  readonly compact = input(false, { transform: booleanAttribute });

  /** The voucher kinds the plugin sells, roughly reflected in the inline sketch. */
  readonly freeAmount = input(true);
  readonly fixedAmounts = input(false);
  readonly products = input(false);

  protected readonly background = computed(() => this.primaryColor() || '#000000');
  protected readonly foreground = computed(() => this.textColor() || '#FFFFFF');
  protected readonly shopTitle = computed(() => this.title() || 'Gutschein auswählen');
  /** Sample amounts in the shop's currency: "25 €", "CHF 25" */
  protected readonly sampleAmounts = computed(() => [2500, 5000].map(formatWholeAmount));
  protected readonly symbol = computed(() => currencySymbol());

  /** Value vouchers are shown first; with none enabled, the products are. */
  protected readonly showMoney = computed(
    () => this.freeAmount() || this.fixedAmounts() || !this.products(),
  );
  /** The free-amount field; also the fallback when no kind is enabled at all. */
  protected readonly showFree = computed(
    () => this.freeAmount() || (!this.fixedAmounts() && !this.products()),
  );
  protected readonly showTabs = computed(
    () => (this.freeAmount() || this.fixedAmounts()) && this.products(),
  );

  /** What the sketch shows, for screen readers. */
  protected readonly description = computed(() =>
    this.variant() === 'widget'
      ? 'Vorschau: Der Button „Gutschein kaufen“ schwebt unten rechts über deiner Website und öffnet den Shop.'
      : 'Vorschau: Der Gutschein-Shop erscheint direkt im Inhalt deiner Seite.',
  );
}
